import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Preloader from "../../components/blocks/Preloader";
import CoursePartnersLogoSection from "../../components/blocks/CoursePartnersLogoSection";
import useSelectedLocation from "../../helpers/useSelectedLocation";
import { getCohortsApi, getCoursesApi } from "../../store/actions";
import { scrollToTop } from "../../utils/scrollToTop";
import Error404 from "../Error404";
import CourseAssociateCoachesSection from "./components/CourseAssociateCoachesSection";
import CourseCertificateSection from "./components/CourseCertificateSection";
import CourseDescriptionSection from "./components/CourseDescriptionSection";
import {
  CourseHeroContent,
  CourseMobileEnrolBar,
  CourseSectionTabs,
  CourseSidebar,
  CourseTopBar,
} from "./components/CourseHeader";
import CourseJoinSection from "./components/CourseJoinSection";
import CoursePaymentSection from "./components/CoursePaymentSection";
import CoursePortfolioSection from "./components/CoursePortfolioSection";
import CourseShell from "./components/CourseShell";
import CourseTestimonialsSection from "./components/CourseTestimonialsSection";
import CourseTrainerSection from "./components/CourseTrainerSection";
import CourseWhatYouWillLearnSection from "./components/CourseWhatYouWillLearnSection";
import {
  aiAutomationAfrica,
  backendProgrammingAfrica,
  blockchainDeveloperProgram,
  businessAnalysisAfrica,
  cloudDevelopmentAfrica,
  cyberSecurity,
  dataAcceleratorAfrica,
  dataScienceFellowshipAfrica,
  frontendProgrammingAfrica,
  fullstackProgrammingAfrica,
  hrAnalyticsData,
  // productDesignAfrica,
  productManagementAcceleratorAfrica,
  productMarketingAfrica,
  techSalesProProgram,
} from "./components/data/africa";
import {
  businessAnalysisEurope,
  cloudDevelopmentEurope,
  dataScienceFellowshipEurope,
  productManagementAcceleratorEurope,
} from "./components/data/europe";
import SiteFooter from "../../components/layout/SiteFooter";

const Course = ({ match }) => {
  const selectedLocation = useSelectedLocation();
  const [selectedCourseData, setSelectedCourseData] = useState(null);
  const [loading, setLoading] = useState(true);

  const renderPageBasedOnSelectedCourseData = (course) => {
    switch (course) {
      case "business-analysis-bootcamp":
        setSelectedCourseData(
          selectedLocation?.name === "Africa" || selectedLocation === undefined
            ? businessAnalysisAfrica
            : businessAnalysisEurope
        );
        break;
      case "blockchain-developer-bootcamp":
        setSelectedCourseData(blockchainDeveloperProgram);
        break;

      case "data-science-bootcamp":
        setSelectedCourseData(
          selectedLocation?.name === "Africa" || selectedLocation === undefined
            ? dataScienceFellowshipAfrica
            : dataScienceFellowshipEurope
        );
        break;

      case "data-analytics-bootcamp":
        setSelectedCourseData(dataAcceleratorAfrica);
        break;
      case "product-management-and-design-bootcamp":
        setSelectedCourseData(
          selectedLocation?.name === "Africa" || selectedLocation === undefined
            ? productManagementAcceleratorAfrica
            : productManagementAcceleratorEurope
        );
        break;

      case "product-marketing-bootcamp":
        setSelectedCourseData(productMarketingAfrica);
        break;
      case "design-bootcamp":
        setSelectedCourseData(productManagementAcceleratorAfrica);
        break;

      case "hr-analytics":
        setSelectedCourseData(hrAnalyticsData);
        break;

      case "frontend-development":
        setSelectedCourseData(frontendProgrammingAfrica);
        break;
      case "backend-development":
        setSelectedCourseData(backendProgrammingAfrica);
        break;
      case "fullstack-development":
        setSelectedCourseData(fullstackProgrammingAfrica);
        break;

      case "cloud-bootcamp":
        setSelectedCourseData(
          selectedLocation?.name === "Africa" || selectedLocation === undefined
            ? cloudDevelopmentAfrica
            : cloudDevelopmentEurope
        );
        break;

      case "cybersecurity-bootcamp":
        setSelectedCourseData(cyberSecurity);
        break;

      case "tech-sales-bootcamp":
        setSelectedCourseData(techSalesProProgram);
        break;

      case "ai-automation-diploma":
        setSelectedCourseData(aiAutomationAfrica);
        break;

      default:
        setSelectedCourseData(null);
        return;
    }
  };

  const [courses, setCourses] = useState([]);
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: 1,
        limit: 20,
      };

      const response = await getCoursesApi(params);
      setCourses(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    const courseName = match.params.slug;

    // setCurrentCourse(courseName);
    renderPageBasedOnSelectedCourseData(courseName);

    scrollToTop();

    fetchData();

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.slug, selectedLocation]);

  const [cohortData, setCohortData] = useState({});
  const [cohortLoading, setCohortLoading] = useState(false);
  const fetchCohortDateData = async () => {
    setCohortLoading(true);
    try {
      const params = { page: 1, limit: 1 };
      const response = await getCohortsApi(params);
      setCohortData(response?.data?.[0] || {});
      setCohortLoading(false);
    } catch (error) {
      setCohortLoading(false);
    }
  };

  useEffect(() => {
    fetchCohortDateData();
  }, []);

  if (loading) {
    return <Preloader />;
  }

  return (
    <div>
      {selectedCourseData ? (
        <div className="pb-20 lg:pb-0">
          <Helmet>
            <title>{selectedCourseData.seoTitle}</title>
            <meta name="description" content={selectedCourseData.metatags} />
            {selectedCourseData.keywords && (
              <meta name="keywords" content={selectedCourseData.keywords} />
            )}
            <meta property="og:title" content={selectedCourseData.seoTitle} />
            <meta
              property="og:description"
              content={selectedCourseData.metatags}
            />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:type" content="website" />
          </Helmet>

          <CourseTopBar
            courses={courses}
            selectedCourseData={selectedCourseData}
          />

          <CourseShell
            sidebar={
              <CourseSidebar
                courses={courses}
                selectedCourseData={selectedCourseData}
                selectedLocation={selectedLocation}
              />
            }
          >
            <CourseHeroContent
              courses={courses}
              selectedCourseData={selectedCourseData}
              selectedLocation={selectedLocation}
              cohortData={cohortData}
              cohortLoading={cohortLoading}
            />

            <CourseSectionTabs />

            <div className="space-y-8 pt-8">
              <CourseDescriptionSection
                selectedCourseData={selectedCourseData}
              />

              <CourseWhatYouWillLearnSection
                selectedCourseData={selectedCourseData}
              />

              <CoursePortfolioSection selectedCourseData={selectedCourseData} />
            </div>
          </CourseShell>

          <CourseCertificateSection
            courses={courses}
            selectedCourseData={selectedCourseData}
          />

          <div className="bg-white py-8 md:py-10">
            <CourseTrainerSection selectedCourseData={selectedCourseData} />
          </div>

          <CourseAssociateCoachesSection
            selectedCourseData={selectedCourseData}
          />

          <div className="bg-white py-8 md:py-10">
            <CoursePaymentSection
              courses={courses}
              loading={loading}
              selectedCourseData={selectedCourseData}
              selectedLocation={selectedLocation}
            />
          </div>

          <CourseTestimonialsSection
            testimonies={selectedCourseData?.studentTestimonies}
          />

          <CoursePartnersLogoSection showNews={false} />

          <CourseJoinSection
            selectedLocation={selectedLocation}
            selectedCourseData={selectedCourseData}
          />

          {/* <CourseOtherInterestSection
            selectedLocation={selectedLocation}
            selectedCourseData={selectedCourseData}
          /> */}

          <SiteFooter />

          <CourseMobileEnrolBar
            courses={courses}
            selectedCourseData={selectedCourseData}
          />
        </div>
      ) : (
        <Error404
          content={
            <>
              Oops! The page you are looking for does not exist.
              <br />
              We don't have this course at the moment. Do check back later
            </>
          }
          linkName="Go back to Home page"
          linkUrl="/"
        />
      )}
    </div>
  );
};

export default Course;
